import React, { useState, useEffect } from 'react';
import { Building2, Search, Award, Check, X, Loader2, AlertCircle, Sparkles, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { captureEvent, identifyUser } from '../utils/posthog';
import { PostHogEvents } from '../utils/posthogEvents';
import {
  extractDegreePrefix,
  calculateScore,
  SEARCH_CONFIG,
  getPrefixVariant
} from '../utils/rncpSearchUtils';

/**
 * Modal d'onboarding de première connexion avec layout 2/3 - 1/3
 * Galerie animée à gauche, contenu à droite
 */
const OnboardingModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // États
  const [step, setStep] = useState('welcome'); // welcome, siret, organization_check, loading, results
  const [siret, setSiret] = useState('');
  const [loading, setLoading] = useState(false);
  const [organism, setOrganism] = useState(null);
  const [organizationExists, setOrganizationExists] = useState(false);
  const [superAdmin, setSuperAdmin] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);
  const [formations, setFormations] = useState([]);
  const [enrichedFormations, setEnrichedFormations] = useState([]);
  const [fusionOpportunities, setFusionOpportunities] = useState([]);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [accessRequestSent, setAccessRequestSent] = useState(false);

  // Slides pour la galerie (placeholder pour l'instant)
  const slides = [
    {
      title: "Analysez vos formations",
      description: "Identifiez automatiquement les opportunités de fusion",
      image: "/images/onboarding/slide1.svg"
    },
    {
      title: "Optimisez votre offre",
      description: "Réduisez les doublons et gagnez en efficacité",
      image: "/images/onboarding/slide2.svg"
    },
    {
      title: "Économisez du temps",
      description: "Automatisez l'analyse de vos certifications RNCP",
      image: "/images/onboarding/slide3.svg"
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);

  // Animation automatique des slides
  useEffect(() => {
    if (isOpen && step === 'loading') {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isOpen, step, slides.length]);

  if (!isOpen) return null;

  // Formatage du SIRET
  const formatSiret = (value) => {
    const numbers = value.replace(/\s/g, '');
    if (numbers.length <= 14) {
      return numbers.replace(/(\d{3})(\d{3})?(\d{3})?(\d{2})?(\d{3})?/, (match, p1, p2, p3, p4, p5) => {
        let result = p1;
        if (p2) result += ' ' + p2;
        if (p3) result += ' ' + p3;
        if (p4) result += ' ' + p4;
        if (p5) result += ' ' + p5;
        return result;
      });
    }
    return value;
  };

  // Rechercher l'organisme par SIRET
  const handleSearchSiret = async () => {
    const cleanSiret = siret.replace(/\s/g, '');

    if (!cleanSiret) {
      toast.error('Veuillez entrer un SIRET');
      return;
    }

    if (cleanSiret.length !== 14 || !/^\d{14}$/.test(cleanSiret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);
    setOrganism(null);

    // Track SIRET submission
    captureEvent(PostHogEvents.SIRET_SUBMITTED, {
      siret_length: cleanSiret.length,
      user_id: user?.id,
      subscription_plan: user?.subscriptionPlan || 'free'
    });

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const data = await response.json();

      console.log('📥 [ONBOARDING] Réponse scrape:', {
        success: data.success,
        hasData: !!data.data,
        totalFormations: data.data?.totalFormations,
        organism: data.data?.organism
      });

      if (data.success && data.data) {
        const orgData = {
          name: data.data.organism?.name || 'Organisme trouvé',
          url: data.data.organismUrl,
          siret: data.data.siret,
          address: data.data.organism?.address,
          totalFormations: data.data.totalFormations
        };

        console.log('🏢 [ONBOARDING] Organisme créé:', orgData);

        setOrganism(orgData);
        setStep('siret');
        toast.success(`Organisme trouvé: ${orgData.name}`);

        // Track successful org search
        captureEvent(PostHogEvents.ORG_SEARCH_RESULT, {
          success: true,
          org_name: orgData.name,
          total_formations: orgData.totalFormations,
          user_id: user?.id,
          subscription_plan: user?.subscriptionPlan || 'free'
        });
      } else {
        toast.error(data.error || 'Organisme non trouvé pour ce SIRET');

        // Track failed search
        captureEvent(PostHogEvents.ORG_SEARCH_RESULT, {
          success: false,
          error: data.error,
          user_id: user?.id
        });
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
      console.error('❌ Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lier l'organisme à l'utilisateur
  const handleLinkOrganization = async () => {
    if (!organism) return;

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/onboarding/link-organization`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            siret: organism.siret,
            organizationName: organism.name,
            totalFormations: organism.totalFormations,
            address: organism.address
          })
        }
      );

      const data = await response.json();

      console.log('🏢 [ONBOARDING] Réponse liaison organisation:', data);

      if (data.success) {
        if (data.organizationExists) {
          // L'organisation existe déjà
          setOrganizationExists(true);
          setSuperAdmin(data.superAdmin);
          setOrganizationData(data.organization);
          setStep('organization_check');

          toast.error('Cet organisme est déjà enregistré sur Orbloom');
        } else {
          // Nouvelle organisation créée avec succès
          toast.success('Organisation créée et liée avec succès !');

          // Passer directement à l'analyse
          handleStartAnalysis();
        }
      } else {
        toast.error(data.error || 'Erreur lors de la liaison de l\'organisation');
      }
    } catch (error) {
      console.error('❌ Erreur liaison organisation:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Demander l'accès à l'organisation existante
  const handleRequestAccess = async () => {
    if (!organizationData) return;

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/onboarding/request-access`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            organizationId: organizationData.id
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        setAccessRequestSent(true);
        toast.success('Demande d\'accès envoyée au super administrateur !');

        // Track access request
        captureEvent(PostHogEvents.ACCESS_REQUEST_SENT, {
          user_id: user?.id,
          organization_id: organizationData.id,
          organization_name: organizationData.name
        });
      } else {
        toast.error(data.error || 'Erreur lors de l\'envoi de la demande');
      }
    } catch (error) {
      console.error('❌ Erreur demande accès:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Lancer l'analyse des formations
  const handleStartAnalysis = async () => {
    if (!organism) return;

    // Vérifier si l'utilisateur est en freemium
    const isFreemium = user?.subscriptionPlan === 'free' || !user?.subscriptionPlan;

    setStep('loading');
    setProgress(0);
    setStatusMessage('Récupération des formations...');
    setFormations([]);
    setEnrichedFormations([]);
    setFusionOpportunities([]);
    setAnalysisComplete(false);

    // Track analysis request
    captureEvent(PostHogEvents.ANALYSIS_REQUESTED, {
      user_id: user?.id,
      subscription_plan: user?.subscriptionPlan || 'free',
      is_freemium: isFreemium,
      total_formations: organism.totalFormations
    });

    try {
      // 1. Récupérer les formations
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/formations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            siret: organism.siret,
            scrapeSource: 'intercariforef'
          })
        }
      );

      const data = await response.json();

      console.log('📥 [ONBOARDING] Réponse formations:', {
        success: data.success,
        formationsCount: data.formations?.length || 0,
        hasFormations: !!data.formations
      });

      if (!data.success || !data.formations) {
        toast.error('Erreur lors de la récupération des formations');
        return;
      }

      const rawFormations = data.formations;
      console.log('📋 [ONBOARDING] Formations brutes:', {
        count: rawFormations.length,
        samples: rawFormations.slice(0, 3).map(f => ({
          title: f.title,
          rncp_code: f.rncp_code
        }))
      });

      setFormations(rawFormations);
      setProgress(20);
      setStatusMessage(`${rawFormations.length} formations trouvées`);

      // 2. Dédupliquer les formations
      await new Promise(resolve => setTimeout(resolve, 500));
      const uniqueFormations = deduplicateFormations(rawFormations);

      console.log('🔄 [ONBOARDING] Déduplication:', {
        avant: rawFormations.length,
        apres: uniqueFormations.length,
        doublons: rawFormations.length - uniqueFormations.length
      });

      setProgress(30);
      setStatusMessage(`${uniqueFormations.length} formations uniques identifiées`);

      // Vérifier la limite freemium (10 formations max)
      if (isFreemium && uniqueFormations.length > 10) {
        // Track freemium limit
        captureEvent(PostHogEvents.FREEMIUM_LIMIT_REACHED, {
          user_id: user?.id,
          formations_count: uniqueFormations.length,
          limit: 10,
          subscription_plan: 'free'
        });

        setStatusMessage(`Limité à 10 formations en version gratuite (${uniqueFormations.length} disponibles)`);
        toast.error(`Version gratuite limitée à 10 formations (${uniqueFormations.length} trouvées)`, {
          duration: 5000
        });
      }

      // Track formations loaded
      captureEvent(PostHogEvents.FORMATIONS_LOADED, {
        user_id: user?.id,
        formations_count: uniqueFormations.length,
        limited: isFreemium && uniqueFormations.length > 10,
        subscription_plan: user?.subscriptionPlan || 'free'
      });

      console.log('🎯 [ONBOARDING] Formations disponibles:', {
        isFreemium,
        total: uniqueFormations.length
      });

      // 3. Enrichir et analyser progressivement (10, 20, 30...)
      await enrichAndAnalyzeProgressive(uniqueFormations, isFreemium);

    } catch (error) {
      console.error('❌ Erreur analyse:', error);
      toast.error('Erreur lors de l\'analyse');
      setStep('siret');
    }
  };

  // Déduplication des formations
  const deduplicateFormations = (formationsArray) => {
    const seen = new Set();
    return formationsArray.filter(f => {
      const key = `${f.title}-${f.rncp_code || ''}`.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Analyse progressive : 10, 20, 30... jusqu'à trouver des fusions
  const enrichAndAnalyzeProgressive = async (allFormations, isFreemium) => {
    const batchSize = 10;
    let currentBatch = 0;
    let totalAnalyzed = 0;
    const allEnriched = [];
    let opportunities = [];

    // Analyser toutes les formations disponibles par lots de 10
    // On ne limite PAS à 10 pour freemium car on cherche des fusions
    const maxFormations = allFormations.length;
    const totalBatches = Math.ceil(maxFormations / batchSize);

    console.log('🚀 [ONBOARDING] Démarrage analyse progressive:', {
      totalFormations: allFormations.length,
      maxFormations,
      batchSize,
      totalBatches,
      isFreemium,
      strategy: 'Analyse par lots jusqu\'à trouver des fusions'
    });

    // Analyser par lots de 10
    while (currentBatch < totalBatches) {
      const startIdx = currentBatch * batchSize;
      const endIdx = Math.min(startIdx + batchSize, maxFormations);
      const batch = allFormations.slice(startIdx, endIdx);

      currentBatch++;

      console.log(`📦 [ONBOARDING] Analyse du lot ${currentBatch}/${totalBatches}:`, {
        startIdx,
        endIdx,
        batchSize: batch.length
      });

      setStatusMessage(`Analyse du lot ${currentBatch}/${totalBatches} (${batch.length} formations)...`);

      // Enrichir et analyser ce lot
      const result = await enrichAndAnalyze(batch, allEnriched);

      // Ajouter les formations enrichies au total
      allEnriched.push(...result.enriched);
      totalAnalyzed += batch.length;

      // Mettre à jour les opportunités
      if (result.opportunities.length > 0) {
        opportunities = result.opportunities;
        setFusionOpportunities(opportunities);

        console.log(`✅ [ONBOARDING] Fusions trouvées dans le lot ${currentBatch}:`, {
          lotNumber: currentBatch,
          formationsAnalyzed: totalAnalyzed,
          fusionsFound: opportunities.length
        });

        // Arrêter si on a trouvé des fusions
        setProgress(100);
        setStatusMessage(`${opportunities.length} opportunité(s) de fusion détectée(s)`);
        setAnalysisComplete(true);
        setEnrichedFormations(allEnriched);
        setTimeout(() => {
          setStep('results');
        }, 1000);
        return;
      }

      console.log(`⚠️  [ONBOARDING] Aucune fusion dans le lot ${currentBatch}, continuation...`);
    }

    // Aucune fusion trouvée après tous les lots
    console.log('❌ [ONBOARDING] Analyse terminée sans fusion:', {
      formationsAnalyzed: totalAnalyzed,
      lotsAnalyzed: currentBatch
    });

    setProgress(100);
    setStatusMessage('Analyse terminée');
    setAnalysisComplete(true);
    setEnrichedFormations(allEnriched);
    setTimeout(() => {
      setStep('results');
    }, 1000);
  };

  // Enrichissement et analyse d'un lot (retourne les opportunités trouvées)
  const enrichAndAnalyze = async (batchFormations, previousEnriched = []) => {
    const enriched = [];
    const opportunities = [];

    // Combiner les formations précédentes et nouvelles pour la comparaison
    const allEnrichedSoFar = [...previousEnriched];

    console.log('🔍 [ONBOARDING] Début analyse du lot:', {
      batchSize: batchFormations.length,
      previousCount: previousEnriched.length,
      totalToCompare: allEnrichedSoFar.length
    });

    for (let i = 0; i < batchFormations.length; i++) {
      const formation = batchFormations[i];

      console.log(`🔍 [ONBOARDING] Formation ${i + 1}/${batchFormations.length}:`, {
        title: formation.title,
        rncp_code: formation.rncp_code
      });

      // Enrichir avec Kit Apprentissage
      const enrichedFormation = await enrichFormation(formation);
      enriched.push(enrichedFormation);
      allEnrichedSoFar.push(enrichedFormation);

      console.log(`✅ [ONBOARDING] Formation enrichie:`, {
        title: enrichedFormation.title,
        rncp_code: enrichedFormation.rncp_code,
        competencesCount: enrichedFormation.competences?.length || 0,
        hasCompetences: !!enrichedFormation.competences && enrichedFormation.competences.length > 0
      });

      // Calculer progression
      const currentProgress = 30 + ((i + 1) / batchFormations.length) * 60;
      setProgress(Math.round(currentProgress));

      // Comparer avec TOUTES les formations enrichies (précédentes + actuelles)
      if (enrichedFormation.competences && enrichedFormation.competences.length > 0) {
        console.log(`🔍 [ONBOARDING] Comparaison avec ${allEnrichedSoFar.length - 1} formations`);

        for (const other of allEnrichedSoFar.slice(0, -1)) {
          if (!other.competences || other.competences.length === 0) {
            console.log(`⏭️  [ONBOARDING] Formation sans compétences, skip:`, other.title);
            continue;
          }

          // Normaliser les codes RNCP pour comparaison (enlever le préfixe RNCP et mettre en minuscules)
          const normalizeRncpCode = (code) => {
            if (!code) return '';
            return code.toString().replace(/^RNCP/i, '').trim().toLowerCase();
          };

          const rncpCode1 = normalizeRncpCode(enrichedFormation.rncp_code);
          const rncpCode2 = normalizeRncpCode(other.rncp_code);

          // Exclure les formations avec le même code RNCP
          if (rncpCode1 && rncpCode2 && rncpCode1 === rncpCode2) {
            console.log(`🚫 [ONBOARDING] Même RNCP détecté, exclusion:`, {
              formation1: enrichedFormation.title,
              formation2: other.title,
              rncp1: enrichedFormation.rncp_code,
              rncp2: other.rncp_code
            });
            continue;
          }

          const commonCompetences = findCommonCompetences(enrichedFormation, other);
          const matchPercentage = (commonCompetences.length / Math.max(enrichedFormation.competences.length, other.competences.length)) * 100;

          console.log(`📊 [ONBOARDING] Comparaison:`, {
            formation1: enrichedFormation.title,
            formation2: other.title,
            rncp1: enrichedFormation.rncp_code,
            rncp2: other.rncp_code,
            comp1Count: enrichedFormation.competences.length,
            comp2Count: other.competences.length,
            commonCount: commonCompetences.length,
            matchPercentage: Math.round(matchPercentage),
            threshold: 50
          });

          if (matchPercentage >= 50) { // Seuil à 50%
            console.log(`🎯 [ONBOARDING] Fusion détectée!`, {
              matchPercentage: Math.round(matchPercentage),
              formation1: enrichedFormation.title,
              formation2: other.title,
              rncp1: enrichedFormation.rncp_code,
              rncp2: other.rncp_code
            });

            opportunities.push({
              formation1: enrichedFormation,
              formation2: other,
              commonCompetences,
              matchPercentage: Math.round(matchPercentage)
            });

            // Ne pas retourner immédiatement, juste stocker l'opportunité
          }
        }
      } else {
        console.log(`⚠️  [ONBOARDING] Formation sans compétences:`, enrichedFormation.title);
      }
    }

    // Retourner les résultats du lot
    console.log('✅ [ONBOARDING] Lot analysé:', {
      formationsAnalyzed: enriched.length,
      opportunitiesFound: opportunities.length,
      formationsWithCompetences: enriched.filter(f => f.competences && f.competences.length > 0).length
    });

    return {
      enriched,
      opportunities
    };
  };

  // Enrichir une formation via Kit Apprentissage (recherche par intitulé)
  const enrichFormation = async (formation) => {
    try {
      console.log(`🔄 [ONBOARDING] Enrichissement:`, {
        title: formation.title,
        rncp_code: formation.rncp_code
      });

      // Étape 1: Rechercher par intitulé dans la BDD locale
      const searchUrl = `${import.meta.env.VITE_API_URL}/rncp/smart-search?q=${encodeURIComponent(formation.title)}&limit=10`;

      console.log(`🔍 [ONBOARDING] Recherche RNCP par intitulé (BDD locale):`, formation.title);

      const searchResponse = await fetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const searchData = await searchResponse.json();

      console.log(`📦 [ONBOARDING] Résultats recherche BDD:`, {
        success: searchData.success,
        resultCount: searchData.data?.certifications?.length || 0,
        firstResult: searchData.data?.certifications?.[0]
      });

      // Si on a trouvé des résultats, prendre le premier (meilleur score)
      if (!searchData.success || !searchData.data?.certifications || searchData.data.certifications.length === 0) {
        console.log(`⚠️  [ONBOARDING] Aucun RNCP trouvé pour:`, formation.title);
        return { ...formation, competences: [], rncp_code: null };
      }

      const bestMatch = searchData.data.certifications[0];
      const rncpCode = bestMatch.rncpCode;

      console.log(`✅ [ONBOARDING] RNCP trouvé:`, {
        rncpCode,
        intitule: bestMatch.title,
        niveau: bestMatch.niveau,
        score: bestMatch.relevanceScore,
        rawMatch: bestMatch
      });

      // Nettoyer le code RNCP (enlever le préfixe "RNCP" si présent)
      const cleanRncpCode = rncpCode.replace(/^RNCP/i, '');

      // Étape 2: SYNCHRONISER la certification pour récupérer blocs + compétences depuis l'API
      console.log(`🔄 [ONBOARDING] Synchronisation de RNCP${cleanRncpCode}...`);

      const syncResponse = await fetch(`${import.meta.env.VITE_API_URL}/rncp/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          rncpCodes: [cleanRncpCode]
        })
      });

      const syncData = await syncResponse.json();

      if (syncResponse.ok && syncData.success) {
        console.log(`✅ [ONBOARDING] RNCP${cleanRncpCode} synchronisé:`, {
          certifications: syncData.data?.details?.certifications,
          blocks: syncData.data?.details?.blocks,
          competences: syncData.data?.details?.competences
        });

        // Délai de 2 secondes pour laisser la base de données se mettre à jour
        await new Promise(resolve => setTimeout(resolve, 2000));
      } else {
        console.warn(`⚠️  [ONBOARDING] Synchronisation échouée pour RNCP${cleanRncpCode}, tentative de lecture BDD...`);
      }

      // Étape 3: Récupérer les détails depuis la BDD locale
      const detailsUrl = `${import.meta.env.VITE_API_URL}/rncp/${cleanRncpCode}/details`;

      console.log(`📥 [ONBOARDING] Récupération détails:`, {
        originalCode: rncpCode,
        cleanedCode: cleanRncpCode,
        url: detailsUrl
      });

      const detailsResponse = await fetch(detailsUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const detailsData = await detailsResponse.json();

      console.log(`📦 [ONBOARDING] Détails RNCP depuis BDD:`, {
        rncpCode: cleanRncpCode,
        success: detailsData.success,
        blocsCount: detailsData.blocks?.length || 0,
        competencesCount: detailsData.competences?.length || 0
      });

      if (detailsData.success && detailsData.competences) {
        // Extraire les intitulés des compétences
        const competences = detailsData.competences.map(c => c.intitule || c.title || c.description);

        return {
          ...formation,
          rncp_code: rncpCode,
          rncp_intitule: bestMatch.intitule || bestMatch.intitule_diplome,
          rncpData: detailsData,
          competences: competences,
          blocs_competences: detailsData.blocks || [],
          nb_blocs: detailsData.blocks?.length || 0,
          nb_competences: competences.length
        };
      }

      console.log(`⚠️  [ONBOARDING] Pas de compétences pour:`, rncpCode);
      return { ...formation, rncp_code: rncpCode, competences: [], blocs_competences: [], rncpData: null };
    } catch (error) {
      console.error(`❌ [ONBOARDING] Erreur enrichissement:`, {
        formation: formation.title,
        error: error.message
      });
      return { ...formation, competences: [], blocs_competences: [] };
    }
  };

  // Trouver compétences communes
  const findCommonCompetences = (f1, f2) => {
    if (!f1.competences || !f2.competences) return [];

    const common = [];
    const f2CompLower = f2.competences.map(c => c.toLowerCase());

    f1.competences.forEach((comp1, idx1) => {
      const comp1Lower = comp1.toLowerCase();
      const idx2 = f2CompLower.findIndex(c => c === comp1Lower);
      if (idx2 !== -1) {
        common.push({
          competence1: comp1,
          competence2: f2.competences[idx2],
          index1: idx1,
          index2: idx2
        });
      }
    });

    return common;
  };

  // Sauvegarder et continuer
  const handleComplete = async () => {
    try {
      // Sauvegarder les données du profil
      await fetch(`${import.meta.env.VITE_API_URL}/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          siret: organism.siret,
          organizationName: organism.name,
          firstLogin: false
        })
      });

      // Track onboarding completion
      captureEvent(PostHogEvents.ONBOARDING_COMPLETED, {
        user_id: user?.id,
        org_name: organism.name,
        formations_analyzed: enrichedFormations.length,
        fusion_opportunities: fusionOpportunities.length,
        subscription_plan: user?.subscriptionPlan || 'free'
      });

      toast.success('Profil configuré avec succès !');
      onClose();
      navigate('/pricing');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Composant CircularProgress avec transformation
  const CircularProgress = ({ progress, isComplete }) => {
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className="relative w-24 h-24 mx-auto mb-4">
        <svg className="transform -rotate-90 w-24 h-24">
          {/* Cercle de fond */}
          <circle
            cx="48"
            cy="48"
            r={radius}
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-gray-200"
          />
          {/* Cercle de progression */}
          <circle
            cx="48"
            cy="48"
            r={radius}
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={`transition-all duration-500 ${
              isComplete ? 'text-green-500' : 'text-primary-500'
            }`}
            strokeLinecap="round"
          />
        </svg>
        {/* Icône centrale */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isComplete ? (
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center animate-scale-in">
              <Check className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
          ) : (
            <span className="text-xl font-bold text-gray-700">{Math.round(progress)}%</span>
          )}
        </div>
      </div>
    );
  };

  // Rendu du contenu selon l'étape
  const renderContent = () => {
    switch (step) {
      case 'welcome':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Bienvenue {user?.firstName} ! 👋
              </h2>
              <p className="text-gray-600">
                Commençons par configurer votre organisme de formation
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-purple-50 rounded-lg p-6 border border-primary-100">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Search className="w-5 h-5 text-primary-600" />
                Rechercher votre organisme
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Entrez le SIRET de votre organisme de formation pour commencer l'analyse
              </p>

              <div className="space-y-3">
                <input
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(formatSiret(e.target.value))}
                  placeholder="000 000 000 00 000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-lg tracking-wider"
                  maxLength={17}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchSiret()}
                />

                <button
                  onClick={handleSearchSiret}
                  disabled={loading || siret.replace(/\s/g, '').length !== 14}
                  className="w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-primary-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Recherche en cours...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      Rechercher
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      case 'siret':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Organisme trouvé !
              </h2>
            </div>

            <div className="bg-white rounded-lg border-2 border-green-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-900">{organism?.name}</h3>
                  {organism?.address && (
                    <p className="text-sm text-gray-600 mt-1">{organism.address}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-2">SIRET: {formatSiret(organism?.siret)}</p>
                  <p className="text-sm text-primary-600 font-medium mt-1">
                    {organism?.totalFormations} formations disponibles
                  </p>
                </div>
              </div>
            </div>

            {/* Message freemium si > 10 formations */}
            {(user?.subscriptionPlan === 'free' || !user?.subscriptionPlan) && organism?.totalFormations > 10 && (
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      Version gratuite - Limite de 10 formations
                    </p>
                    <p className="text-xs text-gray-700">
                      Votre organisme a {organism.totalFormations} formations. Seules les 10 premières seront analysées.
                    </p>
                    <p className="text-xs text-primary-600 font-medium mt-2">
                      Passez à Premium pour analyser toutes vos formations sans limite.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setStep('welcome');
                  setOrganism(null);
                  setSiret('');
                }}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Modifier
              </button>
              <button
                onClick={handleLinkOrganization}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-primary-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-primary-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    Lancer l'analyse
                    <Award className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        );

      case 'organization_check':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Organisation déjà existante
              </h2>
              <p className="text-gray-600">
                Cet organisme est déjà enregistré sur Orbloom
              </p>
            </div>

            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6">
              <p className="text-gray-800 mb-4">
                <strong>{organizationData?.name}</strong> est déjà enregistré sur notre plateforme.
              </p>
              {superAdmin && (
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Super administrateur :</strong>
                  </p>
                  <p className="text-sm text-gray-900">
                    {superAdmin.firstName} {superAdmin.lastName}
                  </p>
                  <p className="text-sm text-gray-600">
                    {superAdmin.email}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-800 mb-3">
                Pour rejoindre cette organisation, vous devez être invité par un administrateur.
              </p>
              {!accessRequestSent ? (
                <button
                  onClick={handleRequestAccess}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-primary-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Demander l'accès au super administrateur
                    </>
                  )}
                </button>
              ) : (
                <div className="bg-green-100 border border-green-300 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-semibold text-green-800">
                      Demande envoyée avec succès !
                    </p>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    L'administrateur a été notifié. Vous recevrez un email lorsqu'il acceptera votre demande.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setStep('welcome');
                  setOrganism(null);
                  setOrganizationData(null);
                  setOrganizationExists(false);
                  setSuperAdmin(null);
                  setAccessRequestSent(false);
                  setSiret('');
                }}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Rechercher un autre SIRET
              </button>
            </div>
          </div>
        );

      case 'loading':
        return (
          <div className="space-y-6 py-8">
            <CircularProgress progress={progress} isComplete={analysisComplete} />

            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {analysisComplete ? 'Analyse terminée !' : 'Analyse en cours...'}
              </h3>
              <p className="text-gray-600">{statusMessage}</p>
            </div>

            {formations.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Formations trouvées</span>
                  <span className="font-semibold text-gray-900">{formations.length}</span>
                </div>
                {enrichedFormations.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Formations analysées</span>
                    <span className="font-semibold text-gray-900">{enrichedFormations.length}</span>
                  </div>
                )}
                {fusionOpportunities.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Opportunités de fusion</span>
                    <span className="font-semibold text-primary-600">{fusionOpportunities.length}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'results':
        return (
          <div className="space-y-6">
            {fusionOpportunities.length === 0 && (
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Analyse terminée !
                </h2>
                <p className="text-gray-600">
                  Aucune opportunité de fusion détectée
                </p>
              </div>
            )}

            {fusionOpportunities.length > 0 ? (
              <div className="space-y-6">
                {fusionOpportunities.map((opp, idx) => (
                  <FusionCard key={idx} opportunity={opp} />
                ))}
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
                <p className="text-gray-700">
                  Aucune opportunité de fusion détectée pour le moment.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Vos formations sont suffisamment distinctes.
                </p>
              </div>
            )}

            <button
              onClick={() => {
                // Track upgrade click
                captureEvent(PostHogEvents.UPGRADE_CLICKED, {
                  user_id: user?.id,
                  source: 'onboarding_results',
                  fusion_opportunities: fusionOpportunities.length,
                  formations_analyzed: enrichedFormations.length,
                  subscription_plan: user?.subscriptionPlan || 'free'
                });
                handleComplete();
              }}
              className="w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-primary-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
            >
              <Sparkles className="w-5 h-5" />
              Continuer vers le tableau de bord
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-hidden flex ${step === 'results' && fusionOpportunities.length > 0 ? 'max-w-[95vw]' : 'max-w-6xl'}`}>

        {/* Partie gauche: Galerie d'images (2/3) - Masquée sur l'écran results avec opportunités */}
        {!(step === 'results' && fusionOpportunities.length > 0) && (
          <div className="hidden lg:block lg:w-2/3 bg-gradient-to-br from-primary-600 via-purple-600 to-pink-600 relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>

            {/* Slides animées */}
            <div className="relative h-full flex items-center justify-center p-12">
              {slides.map((slide, idx) => (
                <div
                  key={idx}
                  className={`absolute inset-0 flex flex-col items-center justify-center p-12 transition-opacity duration-1000 ${
                    currentSlide === idx ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {/* Placeholder pour l'image */}
                  <div className="w-64 h-64 bg-white/20 rounded-full flex items-center justify-center mb-8 backdrop-blur-sm">
                    <Award className="w-32 h-32 text-white/80" />
                  </div>

                  <h2 className="text-4xl font-bold text-white text-center mb-4">
                    {slide.title}
                  </h2>
                  <p className="text-xl text-white/90 text-center max-w-md">
                    {slide.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Indicateurs de slides */}
            <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-2">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentSlide === idx ? 'bg-white w-8' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Partie droite: Contenu (1/3) - Pleine largeur sur results */}
        <div className={`p-8 overflow-y-auto relative ${step === 'results' && fusionOpportunities.length > 0 ? 'w-full' : 'lg:w-1/3 w-full'}`}>
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Contenu dynamique */}
          <div className="mt-8">
            {renderContent()}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from {
            transform: scale(0);
          }
          to {
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Composant pour afficher une opportunité de fusion
const FusionCard = ({ opportunity }) => {
  const { formation1, formation2, commonCompetences, matchPercentage } = opportunity;

  // Récupérer toutes les compétences des deux formations
  const allCompetences1 = formation1.competences || [];
  const allCompetences2 = formation2.competences || [];

  // Créer un Set des compétences communes pour une recherche rapide
  const commonSet1 = new Set(commonCompetences.map(cc => cc.competence1));
  const commonSet2 = new Set(commonCompetences.map(cc => cc.competence2));

  // Fonction pour vérifier si une compétence est commune
  const isCommon1 = (comp) => commonSet1.has(comp);
  const isCommon2 = (comp) => commonSet2.has(comp);

  return (
    <div className="bg-white rounded-lg border-2 border-primary-200">
      {/* En-tête avec pourcentage */}
      <div className="bg-gradient-to-r from-primary-600 to-purple-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold mb-2">Opportunité de fusion détectée</h3>
            <p className="text-primary-100">
              {commonCompetences.length} compétence{commonCompetences.length > 1 ? 's' : ''} commune{commonCompetences.length > 1 ? 's' : ''} sur {Math.max(allCompetences1.length, allCompetences2.length)}
            </p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-4">
            <div className="text-center">
              <div className="text-4xl font-bold">{matchPercentage}%</div>
              <div className="text-sm text-primary-100 mt-1">Similarité</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau comparatif des compétences */}
      <div className="grid grid-cols-2 divide-x divide-gray-200">
        {/* Formation 1 */}
        <div className="p-6">
          <div className="mb-6">
            <h4 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{formation1.title}</h4>
            {formation1.rncp_code && (
              <p className="text-sm text-primary-600 font-semibold mt-2">
                {formation1.rncp_code.toUpperCase().startsWith('RNCP') ? formation1.rncp_code.toUpperCase() : `RNCP${formation1.rncp_code}`}
              </p>
            )}
            {/* Statistiques blocs et compétences */}
            <div className="flex items-center gap-3 text-xs text-gray-600 mt-3">
              <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {formation1.nb_blocs || formation1.blocs_competences?.length || 0} blocs
              </div>
              <div className="bg-green-100 text-green-800 px-2 py-1 rounded">
                {formation1.nb_competences || allCompetences1.length} compétences
              </div>
            </div>
            {/* Affichage des blocs de compétences */}
            {formation1.blocs_competences && formation1.blocs_competences.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-xs font-semibold text-gray-700 uppercase">Blocs de compétences:</p>
                {formation1.blocs_competences.slice(0, 2).map((bloc, bIdx) => (
                  <div key={bIdx} className="bg-gray-50 rounded p-2 text-xs">
                    <div className="font-medium text-gray-800 line-clamp-1">{bloc.intitule || bloc.title}</div>
                    {bloc.nb_competences && (
                      <div className="text-gray-500 mt-0.5">{bloc.nb_competences} compétences</div>
                    )}
                  </div>
                ))}
                {formation1.blocs_competences.length > 2 && (
                  <div className="text-xs text-gray-500 italic">
                    ... et {formation1.blocs_competences.length - 2} autre(s) bloc(s)
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-xs font-medium text-gray-600">Compétences communes</span>
              <div className="w-3 h-3 bg-gray-300 rounded-full ml-4"></div>
              <span className="text-xs font-medium text-gray-600">Compétences spécifiques</span>
            </div>

            {allCompetences1.map((comp, idx) => {
              const isMatch = isCommon1(comp);
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg text-sm transition-all ${
                    isMatch
                      ? 'bg-green-50 border-2 border-green-200 text-green-900 font-medium'
                      : 'bg-gray-50 border border-gray-200 text-gray-500'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {isMatch ? (
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 mt-0.5 flex-shrink-0"></div>
                    )}
                    <span className={isMatch ? '' : 'line-through'}>{comp}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Formation 2 */}
        <div className="p-6">
          <div className="mb-6">
            <h4 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{formation2.title}</h4>
            {formation2.rncp_code && (
              <p className="text-sm text-primary-600 font-semibold mt-2">
                {formation2.rncp_code.toUpperCase().startsWith('RNCP') ? formation2.rncp_code.toUpperCase() : `RNCP${formation2.rncp_code}`}
              </p>
            )}
            {/* Statistiques blocs et compétences */}
            <div className="flex items-center gap-3 text-xs text-gray-600 mt-3">
              <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {formation2.nb_blocs || formation2.blocs_competences?.length || 0} blocs
              </div>
              <div className="bg-green-100 text-green-800 px-2 py-1 rounded">
                {formation2.nb_competences || allCompetences2.length} compétences
              </div>
            </div>
            {/* Affichage des blocs de compétences */}
            {formation2.blocs_competences && formation2.blocs_competences.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-xs font-semibold text-gray-700 uppercase">Blocs de compétences:</p>
                {formation2.blocs_competences.slice(0, 2).map((bloc, bIdx) => (
                  <div key={bIdx} className="bg-gray-50 rounded p-2 text-xs">
                    <div className="font-medium text-gray-800 line-clamp-1">{bloc.intitule || bloc.title}</div>
                    {bloc.nb_competences && (
                      <div className="text-gray-500 mt-0.5">{bloc.nb_competences} compétences</div>
                    )}
                  </div>
                ))}
                {formation2.blocs_competences.length > 2 && (
                  <div className="text-xs text-gray-500 italic">
                    ... et {formation2.blocs_competences.length - 2} autre(s) bloc(s)
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-xs font-medium text-gray-600">Compétences communes</span>
              <div className="w-3 h-3 bg-gray-300 rounded-full ml-4"></div>
              <span className="text-xs font-medium text-gray-600">Compétences spécifiques</span>
            </div>

            {allCompetences2.map((comp, idx) => {
              const isMatch = isCommon2(comp);
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg text-sm transition-all ${
                    isMatch
                      ? 'bg-green-50 border-2 border-green-200 text-green-900 font-medium'
                      : 'bg-gray-50 border border-gray-200 text-gray-500'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {isMatch ? (
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 mt-0.5 flex-shrink-0"></div>
                    )}
                    <span className={isMatch ? '' : 'line-through'}>{comp}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pied avec statistiques */}
      <div className="bg-gray-50 p-4 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-green-600">{commonCompetences.length}</div>
            <div className="text-xs text-gray-600 mt-1">Compétences communes</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-400">
              {allCompetences1.length - commonCompetences.length}
            </div>
            <div className="text-xs text-gray-600 mt-1">Spécifiques à A</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-400">
              {allCompetences2.length - commonCompetences.length}
            </div>
            <div className="text-xs text-gray-600 mt-1">Spécifiques à B</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
